import { RegexValidator } from '$shared/constants/RegexValidator';
import { segmentData } from '$shared/lib/data/SegmentData';

/**
 * Utility class for parsing message templates with parameter substitution and reference resolution
 */
export class MessageTemplateParser {
  /**
   * Parses a message template by replacing parameters and resolving references
   * @param template - The template string to parse
   * @param params - Optional parameters to substitute in the template using {paramName} syntax
   * @returns The parsed message with all substitutions applied
   */
  static parseMessageTemplate(template: string, params?: Record<string, string>): string {
    if (!template) {
      return '';
    }

    // If no parameters provided, only process references
    if (!params) {
      return this.parseReferences(template);
    }

    // Replace parameter placeholders with actual values
    return this.replaceParams(template, params);
  }

  /**
   * Processes all reference types in the template
   * This is the main entry point for reference resolution and can be extended with new reference types
   */
  private static parseReferences(template: string): string {
    let result = template;

    // Apply segment reference parsing rule
    result = this.parseSegmentReferences(result);

    // Future reference types can be added here:

    return result;
  }

  /**
   * Resolves segment references in templates by replacing UUIDs with segment names
   * Segment references are identified by the presence of "segment" text and a UUID pattern
   * This allows templates to reference segments by ID which get resolved to human-readable names
   */
  private static parseSegmentReferences(template: string): string {
    // Quick check to avoid unnecessary processing
    if (!template.includes('segment')) {
      return template;
    }

    // Extract UUID from template using regex validator
    const uuid = template.match(RegexValidator.uuid)?.first() || '';
    if (!uuid) {
      return template;
    }

    // Look up segment by UUID to get its display name
    const segment = segmentData.getSegmentById(uuid);
    if (!segment) {
      return template;
    }

    // Replace UUID with human-readable segment name
    return template.replace(RegexValidator.uuid, segment.name);
  }

  /**
   * Replaces parameter placeholders in template with actual values
   * Parameters use {paramName} syntax and are replaced with corresponding values from params object
   */
  private static replaceParams(template: string, params: Record<string, string>): string {
    let result = template;
    for (const param of Object.keys(params)) {
      const paramValue = params[param] || '';
      // Replace {paramName} with actual parameter value
      result = result.replace(`{${param}}`, paramValue);
    }
    return result;
  }
}
